export type OnboardingMediaCategory = 'design' | 'marketing';

export const ONBOARDING_IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
] as const;
export const ONBOARDING_MAX_FILE_COUNT = 6;
export const ONBOARDING_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const ONBOARDING_UPLOAD_TIMEOUT_MS = 60_000;

export function isOnboardingImageMimeType(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    ONBOARDING_IMAGE_MIME_TYPES.includes(
      value.toLowerCase() as (typeof ONBOARDING_IMAGE_MIME_TYPES)[number]
    )
  );
}

export function onboardingDataUrlMimeType(dataUrl: string): string | null {
  return (
    /^data:([^;,]+)(?:;[^,]*)?,/i.exec(dataUrl)?.[1]?.toLowerCase() ?? null
  );
}

export function isOnboardingMediaCategory(
  value: unknown
): value is OnboardingMediaCategory {
  return value === 'design' || value === 'marketing';
}

type OnboardingMediaUploadArgs<TOrganizationId extends string> = {
  orgSlug: string;
  organizationId: TOrganizationId;
  mediaType: 'image';
  filename: string;
  contentType: string;
};

export type OnboardingMediaUploadRequest<TOrganizationId extends string> =
  | {
      mode: 'private';
      args: OnboardingMediaUploadArgs<TOrganizationId> & {
        flow: 'onboarding-design-thread';
      };
    }
  | {
      mode: 'public';
      args: OnboardingMediaUploadArgs<TOrganizationId> & {
        bucketKind: 'public';
        flow: 'onboarding-marketing-product';
      };
    };

type OnboardingMediaUploadReservation =
  | {
      bucketKind: 'private';
      storageKey: string;
    }
  | {
      bucketKind: 'public';
      storageKey: string;
      publicUrl: string;
    };

export type OnboardingUploadedMedia = {
  storageKey: string;
  url?: string;
  previewUrl?: string;
};

/**
 * Keeps onboarding storage policy tied to the destination that will own the
 * upload. Design onboarding starts a private thread, while product onboarding
 * intentionally keeps the existing public-media flow.
 */
export function buildOnboardingMediaUploadRequest<
  TOrganizationId extends string,
>({
  category,
  orgSlug,
  organizationId,
  filename,
  contentType,
}: {
  category: OnboardingMediaCategory;
  orgSlug: string;
  organizationId: TOrganizationId;
  filename: string;
  contentType: string;
}): OnboardingMediaUploadRequest<TOrganizationId> {
  switch (category) {
    case 'design':
      return {
        mode: 'private',
        args: {
          orgSlug,
          organizationId,
          mediaType: 'image',
          filename,
          contentType,
          flow: 'onboarding-design-thread',
        },
      };
    case 'marketing':
      return {
        mode: 'public',
        args: {
          orgSlug,
          organizationId,
          mediaType: 'image',
          filename,
          contentType,
          bucketKind: 'public',
          flow: 'onboarding-marketing-product',
        },
      };
    default:
      // Preserve a fail-closed runtime boundary if an untyped client ever
      // bypasses the TypeScript category union.
      throw new Error('Unsupported onboarding media category');
  }
}

/**
 * Private upload reservations deliberately have no durable URL. Preserve an
 * existing browser-only preview separately, and expose a persisted URL only
 * when the reservation explicitly came from the public bucket.
 */
export function buildOnboardingUploadedMedia(
  reservation: OnboardingMediaUploadReservation,
  previewUrl?: string
): OnboardingUploadedMedia {
  return {
    storageKey: reservation.storageKey,
    ...(reservation.bucketKind === 'public'
      ? { url: reservation.publicUrl }
      : {}),
    ...(previewUrl ? { previewUrl } : {}),
  };
}
